// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/ban-ts-comment, @typescript-eslint/no-unnecessary-type-assertion, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return, prefer-const, react-hooks/set-state-in-effect */

import _ from "lodash";
import React, { Reducer, useCallback, useEffect, useLayoutEffect, useReducer, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Button, Dropdown, Form, Header, Icon, Modal, Segment, Table } from "semantic-ui-react";

import { DndContext, closestCenter } from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import { SortableContext, arrayMove, horizontalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Tooltip from "rc-tooltip";

import { tooltipDefaults } from "./constants";
import type { LegacyEditorAssessment } from "./utils";
import styles from "./ProtocolSummaryTableEditor.module.css";

/** An assessment as the page passes it in. The editor derives `uniqueId` and `assessmentType` itself. */
export type AssessmentOptionInput = LegacyEditorAssessment;

export type AssessmentOption = AssessmentOptionInput & {
	uniqueId: string; // generated unique id within this component
	assessmentType: string | null;
};

export type AssessmentPresetData = {
	id: string;
	name: string;
	default: boolean;
	sections: {
		id: string;
		sectionName: string;
		displaySectionName: boolean;
		sectionOrder: number;
		presetAnalyses: {
			analysisId: string;
			assessmentOrder: number;
		}[];
		presetCurveModels: {
			curveModelAnalysisId: string;
			analysisId: string | null;
			analysisTypeId: string | null;
			quantifiedRegionTypeId: string | null;
			assessmentOrder: number;
		}[];
		presetGroundData: {
			groundDataAssessmentId: string;
			assessmentOrder: number;
		}[];
	}[];
}[];

export type DataNormalizationOption = {
	key: string;
	value: string;
	text: string;
	disabled: boolean;
};

export type AssessmentPresetOption = {
	key: string;
	text: string;
	value: string;
};

type Integer = number; // for documentation only

type PresetAssessment = {
	assessmentId: string | null; //-- Ground Data
	curveModelAnalysisId: string | null; //-- Curve Models
	analysisId: string | null;
	analysisTypeId: string | null;
};

/**
 * Bitmask enum for allowed directions of move buttons
 */
enum AssessmentMoveDirection {
	None = 0,
	Prev = 1 << 0, // 1 (binary: 0001)
	Next = 1 << 1, // 2 (binary: 0010)
	First = 1 << 2, // 4 (binary: 0100)
	Last = 1 << 3, // 8 (binary: 1000)
}

type SortableAssessmentProps = {
	uniqueId: string; // synthetic id created within protocolSummaryTableEditor
	assessmentName: string | null; // ex: Early Vegetation
	assessmentType: string; // ex: Phenotype
	dataNormalizationId: string; // selected data-norm id, for this record
	dataNormalizationOptions: DataNormalizationOption[];
	hilited: boolean; // assessment has hiliting for drag-n-drop and move affordance
	index: Integer; // cardinal order (1-based index) for display only
	onDataNormalizationChange: (assessmentId: string, opt: DataNormalizationOption) => void; // parent state setter
	onMoveAssessment: (id: string, dir: AssessmentMoveDirection) => void; // parent state setter
	onRemoveFromSelectedAssessments: () => void; // parent state setter
	onRevealButtons: (id: string | null) => void; // parent state setter (toggle)
	plannedTimingName: string | null;
	revealOrderButtons: AssessmentMoveDirection; // assessment ordering buttons, combination of prev/next/first/last
	selected: boolean; // assessment is in the selected subset for Protocol Analysis
};

/**
 * Sortable-draggable wrapper for an assessment (column)
 */
function SortableAssessment({
	uniqueId,
	assessmentName,
	assessmentType,
	dataNormalizationId,
	dataNormalizationOptions,
	hilited,
	index,
	onDataNormalizationChange,
	onMoveAssessment,
	onRevealButtons,
	onRemoveFromSelectedAssessments,
	plannedTimingName,
	revealOrderButtons,
}: SortableAssessmentProps) {
	// state mgmt for drag-n-drop
	const {
		attributes: dragAttributes,
		listeners: dragListeners,
		setNodeRef: setDragNodeRef,
		transform: dragTransform,
		transition: dragTransition,
		isDragging,
	} = useSortable({
		id: uniqueId,
	});

	const scrollRef = useRef(null);

	// Combine setNodeRef and useRef refs using a callback ref
	const combinedRef = useCallback(
		// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
		(node) => {
			setDragNodeRef(node); // For dnd-kit
			scrollRef.current = node; // For scrolling
		},
		[setDragNodeRef],
	);

	// Use useLayoutEffect to scroll after DOM mutations are complete
	useLayoutEffect(() => {
		if (hilited && scrollRef.current) {
			// @ts-expect-error
			scrollRef.current.scrollIntoView({
				behavior: "smooth",
				block: "center",
				inline: "nearest",
			});
		}
	}, [hilited, combinedRef]);

	const wrapperStyle = {
		transform: CSS.Transform.toString(dragTransform),
		dragTransition,
		opacity: isDragging ? 0.5 : 1,
	};

	return (
		<div ref={combinedRef} style={wrapperStyle}>
			<div className={hilited || isDragging ? `${styles.col} ${styles.colHilite}` : styles.col}>
				<div className={`${styles.colRow} ${styles.colRow1}`}>
					{revealOrderButtons === AssessmentMoveDirection.None && (
						<>
							<Tooltip
								{...tooltipDefaults}
								overlay={
									<>
										<Icon name="resize horizontal" size="large" />
										Drag and drop assessment to reorder
									</>
								}
							>
								<Icon
									{...dragAttributes}
									{...dragListeners}
									/* @ts-expect-error dnd-kit types */
									name={isDragging ? "resize horizontal" : "drag-handle-horizontal"}
									size="large"
									className={isDragging ? styles.dragHandleIconActive : styles.dragHandleIconInactive}
								/>
							</Tooltip>
							{index}
							<Tooltip {...tooltipDefaults} overlay={"Assessment order options"}>
								<Button
									icon="options"
									size="small"
									basic
									onClick={() => onRevealButtons(uniqueId)}
									style={{ marginLeft: "0.5rem" }}
								/>
							</Tooltip>
							<Tooltip {...tooltipDefaults} overlay={"Remove from current selections"}>
								<Button
									onClick={() => onRemoveFromSelectedAssessments()}
									icon="trash"
									size="small"
									basic
									style={{ marginLeft: "0.5rem" }}
								/>
							</Tooltip>
						</>
					)}
					{revealOrderButtons > AssessmentMoveDirection.None && (
						<div onMouseLeave={() => onRevealButtons(null)}>
							<Tooltip {...tooltipDefaults} overlay={"Move assessment left"}>
								<Button
									onClick={() => onMoveAssessment(uniqueId, AssessmentMoveDirection.Prev)}
									disabled={!(revealOrderButtons & AssessmentMoveDirection.Prev)}
									icon="angle left"
									size="small"
									basic
								></Button>
							</Tooltip>
							<Tooltip {...tooltipDefaults} overlay={"Move assessment to start"}>
								<Button
									onClick={() => onMoveAssessment(uniqueId, AssessmentMoveDirection.First)}
									disabled={!(revealOrderButtons & AssessmentMoveDirection.First)}
									icon="angle double left"
									size="small"
									basic
								/>
							</Tooltip>
							<Tooltip {...tooltipDefaults} overlay={"Move assessment right"}>
								<Button
									onClick={() => onMoveAssessment(uniqueId, AssessmentMoveDirection.Next)}
									disabled={!(revealOrderButtons & AssessmentMoveDirection.Next)}
									icon="angle right"
									size="small"
									basic
								/>
							</Tooltip>
							<Tooltip {...tooltipDefaults} overlay={"Move assessment to end"}>
								<Button
									onClick={() => onMoveAssessment(uniqueId, AssessmentMoveDirection.Last)}
									disabled={!(revealOrderButtons & AssessmentMoveDirection.Last)}
									icon="angle double right"
									size="small"
									basic
								/>
							</Tooltip>
						</div>
					)}
				</div>

				<div className={`${styles.colRow} ${styles.colRow2}`}>{assessmentName}</div>
				<div className={`${styles.colRow} ${styles.colRow3}`}>{assessmentType}</div>
				<div className={`${styles.colRow} ${styles.colRow4}`}>{plannedTimingName ?? "-"}</div>
				<div className={`${styles.colRow} ${styles.colRow5}`}>
					<Dropdown
						upward={true}
						options={dataNormalizationOptions}
						value={dataNormalizationId}
						onChange={(_, { value }) => {
							const selectedOption = dataNormalizationOptions.find((opt) => opt.key === value);
							if (selectedOption) {
								onDataNormalizationChange(uniqueId, selectedOption);
							}
						}}
					/>
				</div>
			</div>
		</div>
	);
}

const SelectedAssessmentsPlaceholder = () => (
	<Segment
		placeholder
		textAlign="center"
		style={{
			marginTop: 15,
			marginBottom: 15,
			width: "100%",
		}}
	>
		<Header icon>
			<Icon name="folder open outline" style={{ color: "rgba(0,0,0,.6)" }} />
			No assessments selected
		</Header>
		<div>Use the presets menu, or the list below to add assessments.</div>
	</Segment>
);

type AvailableAssessmentsSortableSearchableProps = {
	availableAssessments: AssessmentOption[];
	sortColumn?: string;
	sortDirection?: "ascending" | "descending";
	keywordSearch?: string;
	onAddToSelectedAssessments: (assessment: AssessmentOption) => void;
};

enum ActionType {
	Reset = 0,
	Search = 1,
	Sort = 2,
}

type Direction = "ascending" | "descending";

type Action =
	| { type: ActionType.Reset; newData: AssessmentOption[] }
	| { type: ActionType.Search; keyword?: string }
	| { type: ActionType.Sort; sortColumn: string };

type State = {
	backingData: AssessmentOption[];
	data: AssessmentOption[];
	sortColumn: string;
	sortDirection: Direction;
	keyword?: string;
};

/**
 * Helper function to filter assessment options by keyword
 */
function filterByKeyword(data: AssessmentOption[], keyword?: string): AssessmentOption[] {
	if (!keyword) return data;

	const lowerKeyword = keyword.toLowerCase();
	return data.filter((assessment) =>
		_.find(
			[assessment.assessmentName, assessment.assessmentType, assessment.plannedTimingName],
			(field) => field && field.toLowerCase().includes(lowerKeyword),
		),
	);
}

/**
 * Helper function to sort assessment data
 */
function sortData(data: AssessmentOption[], sortColumn: string, sortDirection: Direction): AssessmentOption[] {
	let sorted = _.sortBy(data, [sortColumn]);
	if (sortDirection === "descending") {
		sorted = sorted.reverse();
	}
	return sorted;
}

const INITIAL_SORT_STATE: State = {
	sortColumn: "assessmentName",
	sortDirection: "ascending",
	// data init is via useEffect
	backingData: [],
	data: [],
};

/**
 * Reducer for AvailableAssessmentsTable component state
 */
// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
function availableAssessmentsTableReducer(state, action: Action): State {
	switch (action.type) {
		case ActionType.Reset: {
			// Update backingData with new data, then reapply current keyword and sort
			let data = action.newData;
			// Initialize with the default sort order (adds stability for sorting by different columns later)
			data = sortData(data, INITIAL_SORT_STATE.sortColumn, INITIAL_SORT_STATE.sortDirection);
			// Reapply keyword filter if one exists
			data = filterByKeyword(data, state.keyword);
			// Reapply sort
			data = sortData(data, state.sortColumn, state.sortDirection);
			return {
				...state,
				backingData: action.newData,
				data,
			};
		}
		case ActionType.Sort: {
			if (state.sortColumn == action.sortColumn) {
				// handle reversing sort of same sortColumn
				const data = state.data.slice().reverse();
				const sortDirection = state.sortDirection === "ascending" ? "descending" : "ascending";
				return { ...state, data, sortDirection };
			}
			// Sort by new column, maintaining current direction
			return {
				...state,
				data: sortData(state.data, action.sortColumn, state.sortDirection),
				sortColumn: action.sortColumn,
			};
		}
		case ActionType.Search: {
			// Filter backing data, then apply current sort
			const filteredData = filterByKeyword(state.backingData, action.keyword);
			const data = sortData(filteredData, state.sortColumn, state.sortDirection);
			return {
				...state,
				data,
				keyword: action.keyword,
			};
		}
	}
}

/**
 * Searchable and sortable table of available assessments.
 */
function AvailableAssessmentsTable({
	availableAssessments,
	keywordSearch,
	onAddToSelectedAssessments,
}: AvailableAssessmentsSortableSearchableProps) {
	const [state, dispatch] = useReducer<Reducer<State, Action>>(availableAssessmentsTableReducer, INITIAL_SORT_STATE);

	useEffect(() => {
		dispatch({ type: ActionType.Reset, newData: [...availableAssessments] });
	}, [availableAssessments]);

	useEffect(() => {
		dispatch({ type: ActionType.Search, keyword: keywordSearch });
	}, [keywordSearch]);

	return (
		<Table celled striped sortable selectable compact textAlign="center">
			<Table.Header style={{ position: "sticky", top: 0, backgroundColor: "white", zIndex: 1 }}>
				<Table.HeaderCell>Add</Table.HeaderCell>
				<Table.HeaderCell
					sorted={state.sortColumn === "assessmentName" ? state.sortDirection : undefined}
					onClick={() => dispatch({ type: ActionType.Sort, sortColumn: "assessmentName" })}
				>
					Assessment Name
				</Table.HeaderCell>
				<Table.HeaderCell
					sorted={state.sortColumn === "assessmentType" ? state.sortDirection : undefined}
					onClick={() => dispatch({ type: ActionType.Sort, sortColumn: "assessmentType" })}
				>
					Assessment Type
				</Table.HeaderCell>
				<Table.HeaderCell
					sorted={state.sortColumn === "plannedTimingName" ? state.sortDirection : undefined}
					onClick={() => dispatch({ type: ActionType.Sort, sortColumn: "plannedTimingName" })}
				>
					Planned Timing
				</Table.HeaderCell>
			</Table.Header>
			<Table.Body>
				{state.data.map((assessment) => (
					<Table.Row key={assessment.uniqueId}>
						<Table.Cell>
							<Tooltip {...tooltipDefaults} placement="right" overlay="Add to current selections">
								<Button icon="plus" size="small" basic onClick={() => onAddToSelectedAssessments(assessment)} />
							</Tooltip>
						</Table.Cell>
						<Table.Cell>{assessment.assessmentName}</Table.Cell>
						<Table.Cell>{assessment.assessmentType}</Table.Cell>
						<Table.Cell>{assessment.plannedTimingName}</Table.Cell>
					</Table.Row>
				))}
			</Table.Body>
		</Table>
	);
}

/**
 * Generate a descriptive label based on the assessmentId and curveModelId.
 */
function generateAssessmentType(assessment: AssessmentOptionInput) {
	if (assessment.assessmentName.includes("Stand Count")) {
		return "Timeseries";
	}
	if (assessment.curveModelId || assessment.curveModelAnalysisId || assessment.iscurvemodel === "true") {
		return assessment.assessmentId.includes("AUC") ? "Phenotype" : "Growth Parameter";
	} else {
		return "Ground Data";
	}
}

/**
 * Generate a unique id by combining assessmentId, planningTimingName. AssessmentId is not always
 * unique, for example with All Ground Data assessment preset.
 */
function generateUniqueId(ao: AssessmentOptionInput): string {
	const { assessmentId, plannedTimingId, plannedTimingName, unitName } = ao;
	return `${assessmentId}-${plannedTimingId}-${plannedTimingName}-${unitName}`;
}

const MODAL_HEIGHT = "95vh";

type ProtocolSummaryTableEditorProps = {
	assessmentOptions: AssessmentOptionInput[];
	assessmentPresetOptions: AssessmentPresetOption[];
	presetData: AssessmentPresetData;
	selectedAssessmentPresetId: string;
	dataNormalizationOptions: DataNormalizationOption[];
	onApply: (edits: AssessmentOption[], assessmentPresetSelection: string | undefined) => void;
	onCancel: () => void;
	open: boolean;
};

/**
 * Summary Table Editor (Uncontrolled). Manages it's own state for the `assessmentOptions` and
 * `selectedAssessmentPreset` props. Notifies parent of results in `OnApply`.
 */
export default function ProtocolSummaryTableEditor({
	assessmentOptions,
	assessmentPresetOptions,
	presetData,
	dataNormalizationOptions,
	onApply,
	onCancel,
	open,
	selectedAssessmentPresetId,
}: ProtocolSummaryTableEditorProps) {
	// working copy, state of current selections and order of assessments: capture initial value lazily from prop.
	const [assessments, setAssessments] = useState<AssessmentOption[]>([]);

	// available assessments (subset which is not in the current selections table)
	const [availableAssessments, setAvailableAssessments] = useState<AssessmentOption[]>([]);

	// state of current preset selection: capture initial value lazily from prop.
	const [selectedAssessmentPreset, setSelectedAssessmentPreset] = useState<string>("none");

	// state of hilited assessment (for illustrating drag-n-drop and re-ordering operations)
	const [hilitedAssessmentId, setHilitedAssessmentId] = useState<string | null>(null);

	// assessment id to reveal swap order buttons (prev, next, etc.)
	const [revealOrderButtonsForAssessmentId, setRevealOrderButtonsForAssessmentId] = useState<string | null>(null);

	// state of ddl having the normalization setting to apply to all rows.
	const [normalizationApplyToAll, setNormalizationApplyToAll] = useState<DataNormalizationOption | null>(null);

	// state of (debounced) keyword search
	const [keywordSearch, setKeywordSearch] = useState<string | undefined>(undefined);
	const debouncedSetKeywordSearch = useRef(_.debounce((val: string) => setKeywordSearch(val), 300)).current;

	// state of (non-debounced) keyword search for the input control
	const [keywordInput, setKeywordInput] = useState("");

	// state of Apply button being enabled (dirty flag)
	const [dirty, setDirty] = useState(false);

	/**
	 * Helper function for broadcasting the data normalization preference to all selected assessments.
	 */
	const updateDataNormalizationToAll = useCallback(() => {
		if (normalizationApplyToAll !== null) {
			setAssessments((prev) =>
				prev!.map((assessment) => ({
					...assessment,
					dataNormalizationId: normalizationApplyToAll.value,
					dataNormalizationName: normalizationApplyToAll.text,
				})),
			);
		} else {
			const noneOption = _.find(dataNormalizationOptions, { text: "None" });
			if (noneOption) {
				setAssessments((prev) =>
					prev!.map((assessment) => ({
						...assessment,
						dataNormalizationId: noneOption.value,
						dataNormalizationName: noneOption.text,
					})),
				);
			}
		}
		setDirty(true);
	}, [normalizationApplyToAll, dataNormalizationOptions]);

	/**
	 * Handle end of dragging event
	 */
	// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
	const onDragEnd = useCallback((event) => {
		const { active, over } = event;

		if (!over) {
			return;
		}

		if (active.id !== over.id) {
			setAssessments((prev) => {
				const oldIndex = prev.findIndex((a) => a.uniqueId === active.id);
				const newIndex = prev.findIndex((a) => a.uniqueId === over.id);
				return arrayMove(prev, oldIndex, newIndex);
			});
			setHilitedAssessmentId(active.id as string);
			setSelectedAssessmentPreset("none");
			setDirty(true);
		}
	}, []);

	/**
	 * Helper function for moving assessment rows around, via buttons affordance
	 */
	const swapAssessment = useCallback((oldIndex: Integer, newIndex: Integer) => {
		setAssessments((prev) => arrayMove(prev, oldIndex, newIndex));
		setDirty(true);
	}, []);

	/**
	 * Handler for the move prev button.
	 */
	const swapAssessmentPrev = useCallback(
		(index: Integer) => {
			if (index <= 0) {
				return;
			}
			const oldIndex = index;
			const newIndex = index - 1;
			swapAssessment(oldIndex, newIndex);
		},
		[swapAssessment],
	);

	/**
	 * Handler for the move next button
	 */
	const swapAssessmentNext = useCallback(
		(index: Integer) => {
			if (index >= assessments.length - 1) {
				return;
			}
			const oldIndex = index;
			const newIndex = index + 1;
			swapAssessment(oldIndex, newIndex);
		},
		[assessments.length, swapAssessment],
	);

	/**
	 * Handler for move to start button
	 */
	const swapAssessmentStart = useCallback(
		(index: Integer) => {
			if (index <= 0) {
				return;
			}
			const oldIndex = index;
			const newIndex = 0;
			swapAssessment(oldIndex, newIndex);
		},
		[swapAssessment],
	);

	/**
	 * Handler for move row to end button
	 */
	const swapAssessmentEnd = useCallback(
		(index: Integer) => {
			if (index >= assessments.length - 1) {
				return;
			}
			const oldIndex = index;
			const newIndex = assessments.length - 1;
			swapAssessment(oldIndex, newIndex);
		},
		[assessments.length, swapAssessment],
	);

	/**
	 * Handle move button clicks
	 */
	const onMoveAssessment = useCallback(
		(id: string, moveDir: AssessmentMoveDirection) => {
			const index = assessments.findIndex((assessment) => assessment.uniqueId === id);
			switch (moveDir) {
				case AssessmentMoveDirection.Prev:
					swapAssessmentPrev(index);
					break;
				case AssessmentMoveDirection.Next:
					swapAssessmentNext(index);
					break;
				case AssessmentMoveDirection.First:
					swapAssessmentStart(index);
					break;
				case AssessmentMoveDirection.Last:
					swapAssessmentEnd(index);
					break;
				default:
					break;
			}
			setHilitedAssessmentId(assessments[index].uniqueId);
		},
		[assessments, swapAssessmentPrev, swapAssessmentNext, swapAssessmentStart, swapAssessmentEnd],
	);

	/**
	 * Handler for change of assessment preset, localized within the editor component.
	 * (logic cribbed from updateAssessmentsWithPreset in ProtocolSummaryTable.js)
	 */
	const onChangeAssessmentPreset = useCallback(
		(id: string) => {
			setSelectedAssessmentPreset(id);

			if (id !== "none") {
				// find matching preset
				let matchingPreset = _.find(presetData, { id });

				if (matchingPreset) {
					// Get all assessments ids to select, based on the preset
					let presetAssessments: PresetAssessment[] = [];
					_.map(matchingPreset.sections, ({ presetAnalyses, presetCurveModels, presetGroundData }) => {
						_.map(presetAnalyses, ({ analysisId }) =>
							presetAssessments.push({
								assessmentId: analysisId,
							} as PresetAssessment),
						);
						_.map(presetGroundData, ({ groundDataAssessmentId }) =>
							presetAssessments.push({
								assessmentId: groundDataAssessmentId,
							} as PresetAssessment),
						);
						_.map(presetCurveModels, ({ curveModelAnalysisId, analysisId, analysisTypeId }) =>
							presetAssessments.push({
								curveModelAnalysisId: curveModelAnalysisId,
								analysisId: analysisId,
								analysisTypeId: analysisTypeId,
							} as PresetAssessment),
						);
					});

					// Start over with the original, ordered list from the parent component.
					const originalAssessments = _.cloneDeep(assessmentOptions).map((assessment) => ({
						...assessment,
						assessmentType: generateAssessmentType(assessment),
						uniqueId: generateUniqueId(assessment),
					}));

					const nextSelectedAssessments = originalAssessments
						.filter((assessment) => {
							return _.some(
								presetAssessments,
								(presetAssessment) =>
									presetAssessment.assessmentId === assessment.assessmentId ||
									(presetAssessment.curveModelAnalysisId === assessment.curveModelAnalysisId &&
										presetAssessment.analysisId === assessment.analysisId &&
										presetAssessment.analysisTypeId === assessment.analysisTypeId),
							);
						})
						.map((assessment) => ({ ...assessment, checked: true }));

					const nextAvailableAssessments = originalAssessments
						.filter((assessment) => {
							return !_.some(
								presetAssessments,
								(presetAssessment) =>
									presetAssessment.assessmentId === assessment.assessmentId ||
									(presetAssessment.curveModelAnalysisId === assessment.curveModelAnalysisId &&
										presetAssessment.analysisId === assessment.analysisId &&
										presetAssessment.analysisTypeId === assessment.analysisTypeId),
							);
						})
						.map((assessment) => ({ ...assessment, checked: false }));

					setAssessments(() => nextSelectedAssessments);
					setAvailableAssessments(() => nextAvailableAssessments);

					if (normalizationApplyToAll) {
						updateDataNormalizationToAll();
					}
					setDirty(true);
				}
			}
		},
		[presetData, assessmentOptions, normalizationApplyToAll, updateDataNormalizationToAll],
	);

	/**
	 * Handler for changing assessment's selected normalization setting.
	 */
	const onDataNormalizationChange = useCallback((id: string, selectedDataNormalization: DataNormalizationOption) => {
		setAssessments((prev) =>
			prev.map((assessment) =>
				assessment.uniqueId === id
					? {
							...assessment,
							dataNormalizationId: selectedDataNormalization.key,
							dataNormalizationName: selectedDataNormalization.text,
						}
					: assessment,
			),
		);
		// reset "apply data normalization to all" setting, because it no longer applies if editing
		// individually.
		setNormalizationApplyToAll(null);
		setDirty(true);
	}, []);

	/**
	 * Handler for which single assessment item should have the move buttons displayed (prev, next, etc.)
	 */
	const onRevealButtons = useCallback((id: string | null) => {
		setRevealOrderButtonsForAssessmentId(id);
	}, []);

	/**
	 * Handle "plus" button in the available assessments table.
	 */
	const onAddToSelectedAssessments = useCallback(
		(assessment: AssessmentOption) => {
			// filter out of available assessments
			setAvailableAssessments((prev) => prev.filter((a) => a.uniqueId !== assessment.uniqueId));
			// spread into selected assessments, and apply any normalization id/name properties
			setAssessments((prev) => [
				...prev,
				{
					...assessment,
					checked: true,
					dataNormalizationId: normalizationApplyToAll?.value ?? assessment.dataNormalizationId,
					dataNormalizationName: normalizationApplyToAll?.text ?? assessment.dataNormalizationName,
				},
			]);
			setHilitedAssessmentId(assessment.uniqueId);
			setSelectedAssessmentPreset("none"); //  turn off presets for manual config
			setDirty(true);
		},
		[normalizationApplyToAll],
	);

	/**
	 * Handle "trash" button in the current selections assessments table.
	 */
	const onRemoveFromSelectedAssessments = useCallback((assessment: AssessmentOption) => {
		// filter out of selected assessments
		setAssessments((prev) => prev.filter((a) => a.uniqueId !== assessment.uniqueId));
		// spread into available assessments (also set checked flag to false)
		setAvailableAssessments((prev) => [...prev, { ...assessment, checked: false }]);
		setHilitedAssessmentId(null);
		setSelectedAssessmentPreset("none"); //  turn off presets for manual config
		setDirty(true);
	}, []);

	/**
	 * Handle Clear All selections button.
	 */
	const onRemoveAllFromSelectedAssessments = useCallback(() => {
		const currentAssessments = assessments; // capture current state
		const currentAvailable = availableAssessments; // capture current state
		const updatedAvailable = [
			...currentAvailable,
			...currentAssessments.map((assessment) => ({ ...assessment, checked: false })),
		];
		setAvailableAssessments(updatedAvailable);
		setAssessments([]);
		setHilitedAssessmentId(null);
		setSelectedAssessmentPreset("none"); // Turn off presets for manual config
		setDirty(true);
	}, [assessments, availableAssessments]);

	/**
	 * Handle change in "apply data normalization to all" ddl.
	 */
	const onChangeDataNormalizationToAll = useCallback(
		(id: string | undefined) => {
			const selectedOption = dataNormalizationOptions.find((opt) => opt.key === id);
			if (selectedOption) {
				setNormalizationApplyToAll(selectedOption);
			}
		},
		[dataNormalizationOptions],
	);

	/**
	 * Helper for rendering the move buttons (prev/next and last/first)
	 */
	const revealButtonsHelper = useCallback(
		(assessmentId: string, index: number): AssessmentMoveDirection => {
			if (revealOrderButtonsForAssessmentId !== assessmentId) {
				return AssessmentMoveDirection.None;
			}
			if (index === 0) {
				return AssessmentMoveDirection.Next | AssessmentMoveDirection.Last;
			}
			if (index >= assessments.length - 1) {
				return AssessmentMoveDirection.Prev | AssessmentMoveDirection.First;
			}
			// Middle items can move in all directions
			return (
				AssessmentMoveDirection.Prev |
				AssessmentMoveDirection.Next |
				AssessmentMoveDirection.First |
				AssessmentMoveDirection.Last
			);
		},
		[revealOrderButtonsForAssessmentId, assessments.length],
	);

	// Reset working copy when the modal is (re)opened with new source data.
	useEffect(() => {
		if (open) {
			const originalAssessments = _.cloneDeep(assessmentOptions).map((assessment) => ({
				...assessment,
				assessmentType: generateAssessmentType(assessment),
				uniqueId: generateUniqueId(assessment),
			}));

			// Guard: Check for duplicate uniqueId values
			const seenIds = new Set<string>();
			const duplicates = new Set<string>();

			originalAssessments.forEach((assessment) => {
				if (seenIds.has(assessment.uniqueId)) {
					duplicates.add(assessment.uniqueId);
				}
				seenIds.add(assessment.uniqueId);
			});

			if (duplicates.size > 0) {
				console.error(
					"[ProtocolSummaryTableEditor] Duplicate uniqueId values detected:",
					Array.from(duplicates),
					"\nAffected assessments:",
					originalAssessments.filter((a) => duplicates.has(a.uniqueId)),
				);
				toast.error("Processing error - see browser console");
			}

			const selectedAssessments = originalAssessments.filter((assessment) => assessment.checked);
			const availableAssessments = originalAssessments.filter((assessment) => !assessment.checked);
			setAssessments(selectedAssessments);
			setAvailableAssessments(availableAssessments);
			setSelectedAssessmentPreset(selectedAssessmentPresetId);
			setNormalizationApplyToAll(null);
			setDirty(false);
		}
	}, [open, assessmentOptions, selectedAssessmentPresetId]);

	// broadcast change of data normalization to all the assessments
	useEffect(() => {
		updateDataNormalizationToAll();
	}, [normalizationApplyToAll, updateDataNormalizationToAll]);

	// Remove highlighted assessment row after 2 seconds (post moving around in the selected
	// assessments table)
	// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
	useEffect(() => {
		if (hilitedAssessmentId !== null) {
			const timer = setTimeout(() => {
				setHilitedAssessmentId(null);
			}, 2000);
			return () => clearTimeout(timer);
		}
	}, [hilitedAssessmentId]);

	return (
		<Modal open={open} size="fullscreen" scrolling="false" centered={false} style={{ height: MODAL_HEIGHT }}>
			<Modal.Header>Summary Table Settings</Modal.Header>
			<Modal.Content basic>
				<div style={{ display: "flex", gap: "0.5em", justifyContent: "flex-end", alignItems: "center" }}>
					<Form.Input>Assessment Presets</Form.Input>
					<Form.Select
						options={assessmentPresetOptions}
						onChange={(_, { value }) => {
							onChangeAssessmentPreset(String(value));
						}}
						value={selectedAssessmentPreset}
					/>
					<Button onClick={onCancel} negative style={{ marginLeft: "2rem" }}>
						Cancel
					</Button>
					<Button
						content="Apply"
						labelPosition="right"
						icon="checkmark"
						onClick={() => {
							// combine the ordered + checked assessments from assessments state, with unchecked
							// options from available assessments
							onApply([...assessments, ...availableAssessments], selectedAssessmentPreset);
						}}
						positive
						disabled={assessments.length === 0 || !dirty}
					/>
				</div>

				{/* Current Selections */}

				<Segment.Group horizontal={true} basic>
					<Segment basic style={{ border: "none" }}>
						<div style={{ display: "flex", alignItems: "center", height: "100%" }}>
							<Header as="h3" style={{ margin: 0 }}>
								Current Selections
							</Header>
						</div>
					</Segment>
					{assessments.length > 0 && (
						<Segment
							size="small"
							basic
							style={{ border: "none", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "2rem" }}
						>
							<Button
								content="Clear All"
								icon="trash"
								basic
								size="small"
								onClick={() => onRemoveAllFromSelectedAssessments()}
							/>
							<div style={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
								<label htmlFor="form-data-normalization-all">Apply Normalization to All:</label>
								<Form.Select
									id="form-data-normalization-all"
									value={normalizationApplyToAll?.value || "n/a"}
									options={[...dataNormalizationOptions, { key: "n/a", value: "n/a", text: "N/A vary by assessment" }]}
									onChange={(_, { value }) => onChangeDataNormalizationToAll(String(value))}
								/>
							</div>
						</Segment>
					)}
				</Segment.Group>
				<Segment.Group basic style={{ border: 0, borderImageWidth: 0, boxShadow: "0 0 0 0" }}>
					{assessments.length === 0 && <SelectedAssessmentsPlaceholder />}
					{assessments.length > 0 && (
						<div style={{ display: "flex", flexDirection: "row" }}>
							<div className={styles.col}>
								<div className={`${styles.colRow} ${styles.colRow1} ${styles.colRowHeading}`}>
									<strong>Order</strong>
								</div>
								<div className={`${styles.colRow} ${styles.colRow2} ${styles.colRowHeading}`}>
									<strong>Assessment Name</strong>
								</div>
								<div className={`${styles.colRow} ${styles.colRow3} ${styles.colRowHeading}`}>
									<strong>Assessment Type</strong>
								</div>
								<div className={`${styles.colRow} ${styles.colRow4} ${styles.colRowHeading}`}>
									<strong>Planned Timing</strong>
								</div>
								<div className={`${styles.colRow} ${styles.colRow5} ${styles.colRowHeading}`}>
									<strong>Data Normalization</strong>
								</div>
							</div>
							<DndContext
								collisionDetection={closestCenter}
								onDragEnd={onDragEnd}
								modifiers={[restrictToHorizontalAxis]}
							>
								<SortableContext
									items={assessments.map((item) => item.uniqueId)}
									strategy={horizontalListSortingStrategy}
								>
									<div className={styles.grid}>
										{assessments.map((assessment, index) => (
											<div className={styles.col} key={assessment.uniqueId}>
												<SortableAssessment
													uniqueId={assessment.uniqueId}
													assessmentName={assessment.assessmentName}
													assessmentType={assessment.assessmentType!}
													// TODO(types): can be null (no normalization chosen), which the Dropdown receives as is
													dataNormalizationId={assessment.dataNormalizationId as string}
													dataNormalizationOptions={dataNormalizationOptions}
													hilited={hilitedAssessmentId === assessment.uniqueId}
													index={index + 1}
													onDataNormalizationChange={onDataNormalizationChange}
													onMoveAssessment={onMoveAssessment}
													onRemoveFromSelectedAssessments={() => onRemoveFromSelectedAssessments(assessment)}
													onRevealButtons={onRevealButtons}
													plannedTimingName={assessment.plannedTimingName}
													revealOrderButtons={revealButtonsHelper(assessment.uniqueId, index)}
													selected={assessment.checked}
												/>
											</div>
										))}
									</div>
								</SortableContext>
							</DndContext>
						</div>
					)}
				</Segment.Group>

				{/* Available Assessments */}

				<Segment.Group horizontal={true} basic style={{ marginTop: 0 }}>
					<Segment basic style={{ border: "none" }}>
						<div style={{ display: "flex", alignItems: "center", height: "100%" }}>
							<Header as="h3" style={{ margin: 0 }}>
								Available Assessments
							</Header>
						</div>
					</Segment>
					<Segment
						size="small"
						basic
						style={{ border: "none", display: "flex", justifyContent: "flex-end", alignItems: "center" }}
					>
						<div style={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
							<Form.Input
								value={keywordInput}
								icon={keywordSearch ? undefined : "search"}
								placeholder="keyword filter"
								onChange={(_, data) => {
									setKeywordInput(data.value);
									debouncedSetKeywordSearch(data.value);
								}}
								action={
									keywordSearch ? (
										<Button
											icon="close"
											onClick={() => {
												setKeywordInput("");
												setKeywordSearch(undefined);
												debouncedSetKeywordSearch.flush();
											}}
											basic
										/>
									) : null
								}
							/>
						</div>
					</Segment>
				</Segment.Group>
				<Segment.Group basic style={{ border: 0, borderImageWidth: 0, boxShadow: "0 0 0 0" }}>
					{/* Vert. scroll the available assessments table (otherwise Modal dialog will scroll the entire page) */}
					<div style={{ maxHeight: `calc(${MODAL_HEIGHT} - 562px)`, overflowY: "auto" }}>
						<AvailableAssessmentsTable
							availableAssessments={availableAssessments}
							keywordSearch={keywordSearch}
							onAddToSelectedAssessments={onAddToSelectedAssessments}
						/>
					</div>
				</Segment.Group>
			</Modal.Content>
		</Modal>
	);
}
